import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { 
  Settings, 
  Lock, 
  Globe, 
  Shield, 
  Edit3,
  Save,
  AlertTriangle,
  Trash2 
} from 'lucide-react';
import { toast } from 'sonner';

interface ChatRoom {
  id: string;
  name: string;
  description?: string | null;
  is_private: boolean;
  created_by: string | null;
  created_at: string | null;
}

interface ChatRoomSettingsProps {
  room: ChatRoom;
  onRoomUpdate?: (updatedRoom: ChatRoom) => void;
  children?: React.ReactNode;
}

export default function ChatRoomSettings({ room, onRoomUpdate, children }: ChatRoomSettingsProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: room.name,
    description: room.description || '',
    is_private: room.is_private,
  });

  const isRoomCreator = user?.id === room.created_by;

  if (!isRoomCreator) {
    return null; // Only show to room creators
  }

  const handleSave = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('chat_rooms')
        .update({
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          is_private: formData.is_private,
        })
        .eq('id', room.id)
        .eq('created_by', user.id) // Extra security check
        .select()
        .single();

      if (error) throw error;

      if (data && onRoomUpdate) {
        onRoomUpdate(data);
      }

      toast.success('Room settings updated successfully!');
      setIsOpen(false);
    } catch (error) {
      console.error('Error updating room:', error);
      toast.error('Failed to update room settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrivacyToggle = (checked: boolean) => {
    setFormData(prev => ({ ...prev, is_private: checked }));
  };

  const handleDeleteRoom = async () => {
    if (!user) return;
    
    try {
      setDeleting(true);
      
      const { error } = await supabase
        .from("chat_rooms")
        .delete()
        .eq("id", room.id)
        .eq("created_by", user.id); // Extra security check

      if (error) {
        console.error("Error deleting chat room:", error);
        toast.error("Failed to delete chat room");
        return;
      }

      toast.success("Chat room deleted successfully");
      navigate("/chat-rooms");
      
    } catch (err) {
      console.error("Error deleting chat room:", err);
      toast.error("Failed to delete chat room");
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button variant="ghost" size="sm">
            <Settings className="h-4 w-4" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px] max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Room Management
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4 pr-2">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Edit3 className="h-4 w-4" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="roomName">Room Name</Label>
                <Input
                  id="roomName"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter room name..."
                  maxLength={50}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="roomDescription">Description (Optional)</Label>
                <Textarea
                  id="roomDescription"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe what this room is about..."
                  maxLength={200}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Privacy Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Privacy & Access
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    {formData.is_private ? (
                      <Lock className="h-4 w-4 text-orange-500" />
                    ) : (
                      <Globe className="h-4 w-4 text-green-500" />
                    )}
                    <Label className="text-base font-medium">
                      {formData.is_private ? 'Private Room' : 'Public Room'}
                    </Label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {formData.is_private 
                      ? 'Only invited members can join and see messages'
                      : 'Anyone can join and participate in this room'
                    }
                  </p>
                </div>
                <Switch
                  checked={formData.is_private}
                  onCheckedChange={handlePrivacyToggle}
                />
              </div>

              {/* Privacy Warning */}
              {room.is_private !== formData.is_private && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-yellow-800 dark:text-yellow-200">
                      Changing Privacy Settings
                    </p>
                    <p className="text-yellow-700 dark:text-yellow-300 mt-1">
                      {formData.is_private 
                        ? 'Making the room private will require users to be invited to participate.'
                        : 'Making the room public will allow anyone to join and see all messages.'
                      }
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Separator />

          {/* Danger Zone */}
          <Card className="border-destructive/20">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-4 w-4" />
                Danger Zone
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="font-medium">Delete Room</p>
                  <p className="text-sm text-muted-foreground">
                    Permanently delete this room and all its messages. This cannot be undone.
                  </p>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                  disabled={isLoading || deleting}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Room
                </Button>
              </div>
            </CardContent>
          </Card>

        </div>

        {/* Action Buttons - Fixed at bottom */}
        <div className="flex-shrink-0 border-t pt-4 mt-4">
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => setIsOpen(false)}
              disabled={isLoading || deleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isLoading || deleting || !formData.name.trim()}
              className="flex items-center gap-2"
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Changes
            </Button>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        {showDeleteDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background border rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-2">Delete Chat Room</h3>
              <p className="text-muted-foreground mb-4">
                Are you sure you want to delete "{room.name}"? This action cannot be undone. 
                All messages, members, and invitations will be permanently removed.
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteDialog(false)}
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteRoom}
                  disabled={deleting}
                >
                  {deleting ? (
                    <div className="flex items-center space-x-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      <span>Deleting...</span>
                    </div>
                  ) : (
                    "Delete Room"
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}